/**
 * Доля хвоста публичного рендерера: вид `number_input` (поле числа).
 *
 * WEB-2920, заход 4: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово. Метка `case 'number_input'` остаётся в фасаде; ветка вида — функция
 * отрисовки `renderNumberInput(ctx)`. Замыкания фасада приходят объектом-контекстом
 * (`ExtendedRenderContext`); ответ посетителя ветка читает из `values` и пишет
 * через `setValue` — это `useFormValues()` фасада: хук стоит до `switch`, в
 * функции отрисовки его звать нельзя (рисуется одна ветка за рендер). Эту
 * долю импортирует только фасад: она едет в ленивом куске `extended`, а не в
 * первом экране (самопроверка обхода).
 */
import React from 'react';
// WEB-1694: оформление текста ВНУТРИ поля ввода отдельно от подписи над ним
// (порядок силы слоёв — в inputFieldStyle.ts).
import {
  inputLabelStyleOverride,
  inputTextColorOverride,
  inputTextFontSizeOverride,
  // Слово владельца 21.10.2026: вид самого поля (фон, рамка, скругление,
  // высота, отступ, тень, подсказка, рамка в фокусе).
  inputBoxStyleOverride,
  inputLookClassName,
} from '../inputFieldStyle';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';

export function renderNumberInput(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const { block, varCtx, interp, values, setValue } = ctx;
  return (
    <div style={{ marginBottom: 16 }}>
      {block.content && (
        <label
          style={{
            display: 'block',
            fontWeight: 600,
            marginBottom: 8,
            color: 'var(--color-text-primary, inherit)',
            // WEB-1694: свой цвет и кегль ПОДПИСИ — накладкой ПОСЛЕ
            // строки выше. Пустые поля спредят {}: вид прежний до байта.
            ...inputLabelStyleOverride(block),
          }}
        >
          {interp(block.content)}
        </label>
      )}
      <input
        type="number"
        placeholder={
          // WEB-2923: подсказка автора — через дверь подстановки (`interp`).
          (block.placeholder == null ? undefined : interp(block.placeholder)) ??
          // WEB-1907 (добор): пустое поле — это старая воронка, блок из
          // ИИ-генерации или стёртый автором текст. Английский литерал
          // тут показывал «Enter number» покупателю русской воронки;
          // слово берём из того же словаря по языку воронки, что и
          // умолчание нового блока (blockDefaults.numberInputPlaceholder).
          getPublicQuizStrings(varCtx?.system?.locale).blockDefaults
            .numberInputPlaceholder
        }
        min={block.numberMin}
        max={block.numberMax}
        value={values[block.id] ?? ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setValue(block.id, e.target.value)
        }
        data-testid={`public-input-${block.id}`}
        className={inputLookClassName(block)}
        style={{
          width: '100%',
          padding: '10px 14px',
          border: '2px solid var(--color-border-primary, #d9d9d9)',
          borderRadius: 'var(--border-radius-md, 8px)',
          fontSize: 16,
          boxSizing: 'border-box',
          background: 'var(--color-bg-surface, #fff)',
          color:
            'var(--color-text-on-surface, var(--color-text-primary, inherit))',
          // WEB-1694: свой кегль и цвет ТЕКСТА ВНУТРИ поля — накладками
          // ПОСЛЕ зашитых строк выше. Пустые поля спредят {}.
          // Слово владельца 21.10.2026: вид САМОГО поля — накладкой ПОСЛЕ
          // зашитых строк выше; пусто — {}, решает стиль-пак.
          ...inputBoxStyleOverride(block),
          ...inputTextFontSizeOverride(block),
          ...inputTextColorOverride(block),
        }}
      />
    </div>
  );
}
